/**
 * Client-side uploader wizard: parse DX + patient CSVs, column mapping, literal fallbacks, output preview.
 * "Create enrollments" POSTs JSON to /uploader/create_enrollments (one Whiteglove + N Enrollments).
 */
import Papa from "papaparse"
import {
  coalesceMobileHome,
  coalesceMobileHomeOther,
  dobNormalize,
  dobPadYear,
  fetchGeocodeJson,
  isPoboxAddress,
  parseGeocodeResult,
  stripLineBreaks
} from "./uploader_scrub"
import { withBusy } from "./app_busy"
import {
  UPLOADER_PREVIEW_CSV_HEADERS,
  computeUploaderCohortPreview,
  parseDxDeviceMapJson,
  previewCsvCell
} from "./uploader_cohort_preview"

let enrollmentFields = []
/** @type {{ key_name: string, name: string }[]} */
let signatureMethodOptions = []

const DEFAULT_SIGNATURE_METHOD_KEY = "PHYSICIAN_ATTESTATION"
/** Matches Enrollment default_values; shown in literals table and sent when language is not mapped. */
const DEFAULT_LANGUAGE_LITERAL = "English"

/** Excluded from the "Default literals" table (and patient CSV column options). */
const LITERALS_EXCLUDE = new Set([
  "mapped_availity_id",
  "availity_plan_id",
  "availity_plan_number",
  "availity_plan_description",
  "availity_plan_json",
  "drchrono_plan_description",
  "availity_coverage_json",
  "partb_status",
  "state_of_check",
  "drchrono_appoinment_results",
  "is_active_mbi",
  "is_legacy_mbi",
  "is_dx_match",
  "is_med_adv",
  "is_no_insurance_match",
  "is_sixty_five_plus",
  "dx_requires_approval",
  "enrollable_medicare",
  "verifiable_medadv",
  "no_dx_or_insurance",
  "has_dx_no_insurance",
  "no_dx_has_insurance",
  "has_prev_dx_codes",
  "has_latest_dx_codes",
  "qualified_dx_codes",
  "missing_demographics",
  "data_errors",
  "eligibilityresult",
  "dr_chrono_patient_id",
  "dr_chrono_patient_chart",
  "dr_chrono_appointment_id",
  "dr_chrono_appointment_description",
  "dr_chrono_rpm_notes",
  "dx_codes"
])

/** Hidden only from the Default literals table (Patient tab); not used for column mapping / output columns. */
const LITERAL_FIELD_EXCLUDE = new Set([
  "devices",
  "phone_3_number",
  "created_at",
  "updated_at",
  "pid",
  "age"
])

function literalFieldExcluded(value) {
  if (LITERAL_FIELD_EXCLUDE.has(value)) return true
  // Chart / MRN column: map from patient CSV only (whiteglove EHR is the #uploader-ehr UUID).
  if (value === "patient_ehr_id") return true
  // Secondary / tertiary: mappable from CSV only, never default literals.
  if (value.startsWith("secondary_") || value.startsWith("tertiary_")) return true
  return false
}

/** Excluded from patient column map dropdown, output columns, and literals (system / non-demographic fields). */
function patientUploaderExcludedField(value) {
  if (
    value === "created_at" ||
    value === "updated_at" ||
    value === "pid" ||
    value === "devices" ||
    value === "age"
  ) {
    return true
  }
  return false
}

/** Base list: enrollment columns usable in uploader (minus system / computed fields in LITERALS_EXCLUDE). */
function baseMappableEnrollmentFields() {
  return enrollmentFields.filter((f) => !LITERALS_EXCLUDE.has(f.value))
}

/** Patient file column mapping targets (excludes fields we only set elsewhere + signature_method literal-only). */
function patientColumnMapFields() {
  return baseMappableEnrollmentFields().filter(
    (f) => !patientUploaderExcludedField(f.value) && f.value !== "signature_method"
  )
}

/** Default literals table: not in literal-only excludes and not reserved for column-map-only exclusions. */
function patientLiteralFieldRows() {
  return baseMappableEnrollmentFields().filter(
    (f) => !literalFieldExcluded(f.value) && !patientUploaderExcludedField(f.value)
  )
}

/** Compact map tables: nowrap; light zebra + hover. */
const UMAP_TABLE = "app-data-table uploader-map-table w-full min-w-0 table-auto border-collapse text-xs"
/** Map preview on DX + Patient: grow with column content, scroll horizontal (wider) instead of squashing (taller / unusable). */
const UMAP_TABLE_MAP =
  "app-data-table uploader-map-table w-max min-w-full table-auto border-collapse text-xs"
const UMAP_THEAD = "text-xs"
const UMAP_TH = "whitespace-nowrap border-b border-gray-200/90 bg-gray-50/95 px-1.5 py-0.5 text-left align-bottom font-medium uppercase leading-tight text-gray-600"
const UMAP_HLABEL = "mb-0.5 block whitespace-nowrap"
const UMAP_TBODY_TR = "even:bg-white odd:bg-slate-50/55 border-b border-gray-100/80 hover:bg-rose-50/25"
/** No whitespace-nowrap: layout uses this + nowrap only where we need a fixed narrow column. */
const UMAP_TD_BASE =
  "box-border min-w-0 border-b border-gray-100/80 px-1.5 py-0.5 text-left text-xs leading-snug text-gray-900"
const UMAP_TD = `${UMAP_TD_BASE} whitespace-nowrap`
/**
 * Output tab: one line per cell, size columns to at least the longest value (wider, horizontal scroll; no squish).
 * Used with UMAP_TABLE_MAP on the table so the grid grows with content instead of breaking/wrapping.
 */
const UMAP_OUT_DATA_TD =
  "box-border border-b border-gray-100/80 px-1.5 py-0.5 text-left align-top text-xs leading-snug text-gray-900 whitespace-nowrap min-w-max"
/** Output: DX codes column (comma-separated, single line; table scrolls horizontally if needed). */
const UMAP_OUT_TD_DX =
  "box-border min-w-[14rem] border-b border-gray-100/80 px-1.5 py-0.5 text-left align-top text-xs text-gray-900 whitespace-nowrap"
/** Output: Unique ID and patient ehr (mono; no w-[1%] so long ids are not squished). */
const UMAP_OUT_TD_MONO =
  "box-border min-w-max border-b border-gray-100/80 px-1.5 py-0.5 text-left font-mono text-[11px] text-gray-900 whitespace-nowrap"
const UMAP_SELECT = "uploader-mapped-select w-full min-w-0 max-w-[16rem] rounded-sm border border-gray-200 bg-white py-0.5 pl-1 pr-0.5 text-[11px] leading-tight"

function normHeaderCompact(s) {
  return String(s ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "")
}

const HEADER_ALIAS_TO = {
  matching: "__UNIQUE__",
  patientid: "__UNIQUE__",
  atienid: "__UNIQUE__",
  mrn: "__UNIQUE__",
  memberid: "__UNIQUE__",
  emrn: "__UNIQUE__",
  uniqueid: "__UNIQUE__",
  patientnumber: "__UNIQUE__",
  pid: "__UNIQUE__",
  dx: "__DX__",
  patientfirstname: "first_name",
  patientlastname: "last_name",
  firstname: "first_name",
  lastname: "last_name",
  patientdob: "dob",
  patientdateofbirth: "dob",
  dateofbirth: "dob",
  patientemail: "email",
  emailaddress: "email",
  patientemailaddress: "email",
  patientaddressline1: "street",
  patientaddressline2: "street",
  patientaddress: "street",
  patientmailingstreetaddress: "street",
  // Plain "Address" / "Street" (normHeaderCompact) → street
  address: "street",
  street: "street",
  patientcity: "city",
  patientstate: "state", // e.g. Patient_State, patient_state
  patientzip: "zip_code", // e.g. Patient_Zip (compact is patientzip, not patientzipcode)
  patientzipcode: "zip_code",
  // "Zip" vs "zip_code" (compact: zip / zipcode)
  zip: "zip_code",
  zipcode: "zip_code",
  patienthomephone: "primary_phone",
  patientworkphone: "phone_2_number",
  patientmobile: "phone_2_number",
  patientmobileno: "phone_2_number",
  mobilephone: "phone_2_number",
  workphone: "phone_2_number",
  // "Home phone" (compact: homephone) and similar → primary
  homephone: "primary_phone",
  hometel: "primary_phone",
  hometelephone: "primary_phone",
  homephonenumber: "primary_phone",
  phone: "primary_phone",
  primaryphysicianemail: "provider_email",
  primaryinurance: "primary_insurance_name", // e.g. PrimaryINsurance Name
  primaryinsurancen: "primary_insurance_name", // Patient Mailing "Primary Insurance" style
  primaryinursanceid: "primary_insurance_number", // "PrimaryINsurance Id" → …ursanceid (typos in exports)
  primaryinursancegroup: "primary_insurance_group_num",
  primaryinursancepolic: "primary_insurance_number", // long token from sample
  primaryinursancepol: "primary_insurance_number", // "PrimaryINsurance...Number"
  secondaryinursance: "secondary_insurance_provider_name",
  secondaryinursancegroup: "secondary_insurance_group_number",
  secondaryinursanceind: "secondary_insurance_individual_number",
  // Common exports: "patient ..." with spaces/underscores
  patientsex: "gender",
  patientprimaryinsurancename: "primary_insurance_name",
  patientprimaryinsuranceid: "primary_insurance_number",
  patientprimaryinsurancegroup: "primary_insurance_group_num",
  patientsecondaryinsurancename: "secondary_insurance_provider_name",
  patientsecondaryinsuranceid: "secondary_insurance_individual_number",
  patientsecondaryinsurancegroup: "secondary_insurance_group_number",
  patienttertiaryinsurancename: "tertiary_insurance_provider_name",
  patienttertiaryinsuranceid: "tertiary_insurance_individual_number",
  patienttertiaryinsurancegroup: "tertiary_insurance_group_number",
  // Without "patient" prefix (e.g. tab-separated "primary insurance name")
  primaryinsurancename: "primary_insurance_name",
  primaryinsuranceid: "primary_insurance_number",
  primaryinsurancegroup: "primary_insurance_group_num",
  secondaryinsurancename: "secondary_insurance_provider_name",
  secondaryinsuranceid: "secondary_insurance_individual_number",
  secondaryinsurancegroup: "secondary_insurance_group_number",
  tertiaryinsurancename: "tertiary_insurance_provider_name",
  tertiaryinsuranceid: "tertiary_insurance_individual_number",
  tertiaryinsurancegroup: "tertiary_insurance_group_number",
  // EHR exports: "DX Code", "Pat …", "Pri …", "Sec …"
  dxcode: "__DX__",
  patfirstname: "first_name",
  patlastname: "last_name",
  patdob: "dob",
  pripayorname: "primary_insurance_name",
  prigroupnumber: "primary_insurance_group_num",
  prisubscrnumber: "primary_insurance_number",
  secpayorname: "secondary_insurance_provider_name",
  secgroupnumber: "secondary_insurance_group_number",
  secsubscrnumber: "secondary_insurance_individual_number"
}

function enrollmentKeyCompact(fv) {
  return String(fv)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "")
}

function patientFileDxByCompact(c) {
  if (c === "dx") return true
  if (/^diagnosiscodename\d*$/i.test(c)) return true
  if (c.length >= 8 && c.indexOf("diagnosiscod") === 0) return true
  if (/^icd/i.test(c) || c === "icd10" || c === "icd9" || c === "icd") return true
  return false
}

function dxFileColumnIsDx(c) {
  if (c === "dx" || c === "dxcode" || c === "icd" || c === "icd10" || c === "diagnosis") return true
  if (/^diagnosiscodename\d*$/i.test(c)) return true
  if (c.length >= 6 && c.indexOf("diagnosis") === 0) return true
  if (c.indexOf("diagnosiscod") === 0) return true
  if (/^icd/i.test(c)) return true
  return false
}

function automapPatientFileRoles(headers) {
  const m = /** @type {Record<string, string>} */({})
  if (!headers || !headers.length) return m
  const mappable = new Set(patientColumnMapFields().map((f) => f.value))
  let uIdx = -1
  for (let i = 0; i < headers.length; i++) {
    const c = normHeaderCompact(headers[i])
    const a = /** @type {string | undefined} */ (/** @type {any} */ (HEADER_ALIAS_TO)[c])
    if (a === "__UNIQUE__" || c === "matching" || c === "patientid" || c === "atienid") {
      uIdx = i
      break
    }
  }
  if (uIdx < 0) {
    for (let i = 0; i < headers.length; i++) {
      const c = normHeaderCompact(headers[i])
      if (c && (c.includes("patientid") || c === "mrn" || c === "memberid" || c === "pid" || c === "emrn")) {
        uIdx = i
        break
      }
    }
  }
  if (uIdx < 0) {
    for (let i = 0; i < headers.length; i++) {
      const c = normHeaderCompact(headers[i])
      if (
        c === "patientid" ||
        c === "atienid" ||
        c === "atientsid" ||
        c === "matching" ||
        c === "mrn" ||
        c === "memberid" ||
        c === "emrn" ||
        c === "uniqueid" ||
        c === "pid" ||
        c === "patientnumber" ||
        c.indexOf("patientid") === 0
      ) {
        uIdx = i
        break
      }
    }
  }
  if (uIdx < 0) uIdx = 0

  for (const h of headers) {
    const c = normHeaderCompact(h)
    if (h === headers[uIdx]) {
      m[h] = "unique_id"
      continue
    }
    const a = /** @type {string | undefined} */ (/** @type {any} */ (HEADER_ALIAS_TO)[c])
    if (a === "__DX__" || c === "dx" || patientFileDxByCompact(c)) {
      m[h] = "dx"
      continue
    }
    if (a && a !== "__UNIQUE__" && a !== "__DX__" && mappable.has(a)) {
      m[h] = a
      continue
    }
    let set = false
    for (const f of mappable) {
      if (c === enrollmentKeyCompact(f)) {
        m[h] = f
        set = true
        break
      }
    }
    if (set) continue
    m[h] = "ignore"
  }
  return m
}

function automapDxFileRoles(headers) {
  const m = /** @type {Record<string, string>} */({})
  if (!headers?.length) return m
  m[headers[0]] = "unique_id"
  for (let i = 1; i < headers.length; i++) {
    const h = headers[i]
    const c = normHeaderCompact(h)
    m[h] = dxFileColumnIsDx(c) ? "dx" : "ignore"
  }
  return m
}

function uniqueOrdered(list) {
  const seen = new Set()
  const o = []
  for (const x of list) {
    if (seen.has(x)) continue
    seen.add(x)
    o.push(x)
  }
  return o
}

function collectPatientDxFromRow(patientRow) {
  const out = /** @type {string[]} */ ([])
  for (const h of Object.keys(state.patientColRole)) {
    if (state.patientColRole[h] !== "dx") continue
    const v = patientRow[h]
    if (v == null || String(v).trim() === "") continue
    String(v)
      .split(/[,;|]/)
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((t) => out.push(t))
  }
  return out
}

const state = {
  practiceName: "",
  providerEmail: "",
  ehrId: "",
  /** Label from #uploader-ehr selected option (preview only; DB still uses ehrId). */
  ehrName: "",
  notesHtml: "",
  dx: { headers: [], rows: [] },
  patient: { headers: [], rows: [] },
  dxColRole: {},
  patientColRole: {},
  literals: {},
  /** @type {Record<string, { line1: string, city: string | null, state: string | null, zipCode: string | null }>} */
  geocodeByUid: {},
  scrub: {
    stripLineBreaks: true,
    hideEmptyUid: true,
    dob: "normalize",
    dobDelimiter: "/",
    phone: "off",
    addressGeocode: false
  }
}

function parseCsvFile(file) {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: "greedy",
      complete: (res) => resolve(res),
      error: (err) => reject(err)
    })
  })
}

function defaultDxRoles(headers) {
  const m = {}
  headers.forEach((h, i) => {
    if (i === 0) m[h] = "unique_id"
    else if (i === 1) m[h] = "dx"
    else m[h] = "ignore"
  })
  return m
}

function defaultPatientRoles(headers) {
  const m = {}
  headers.forEach((h) => {
    m[h] = "ignore"
  })
  return m
}

function escapeHtml(s) {
  const d = document.createElement("div")
  d.textContent = s == null ? "" : String(s)
  return d.innerHTML
}

function escapeAttr(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
}

function patientColumnSelectOptions(selected) {
  let o = '<option value="">—</option>'
  o += `<option value="unique_id" ${selected === "unique_id" ? " selected" : ""}>Unique ID</option>`
  o += `<option value="dx" ${selected === "dx" ? " selected" : ""}>DX (→ dx codes)</option>`
  o += `<option value="ignore" ${selected === "ignore" ? " selected" : ""}>Ignore</option>`
  patientColumnMapFields().forEach((f) => {
    o += `<option value="${escapeAttr(f.value)}" ${selected === f.value ? " selected" : ""}>${escapeHtml(
      f.label
    )}</option>`
  })
  return o
}

function readLiteralValuesFromDom(root) {
  const out = { ...state.literals }
  root.querySelectorAll("[data-uploader-literal-field]:not([disabled])").forEach((inp) => {
    const f = inp.getAttribute("data-uploader-literal-field")
    if (!f) return
    out[f] = "value" in inp ? inp.value : ""
  })
  state.literals = out
}

function signatureMethodSelectOptionsHtml(selectedKey) {
  const keys = new Set()
  /** @type {{ key_name: string, name: string }[]} */
  const ordered = []
  for (const sm of signatureMethodOptions) {
    const k = sm.key_name == null ? "" : String(sm.key_name).trim()
    if (!k || keys.has(k)) continue
    keys.add(k)
    ordered.push({ key_name: k, name: sm.name == null || String(sm.name).trim() === "" ? k : String(sm.name) })
  }
  ordered.sort((a, b) => a.name.localeCompare(b.name))
  if (!keys.has(DEFAULT_SIGNATURE_METHOD_KEY)) {
    ordered.unshift({
      key_name: DEFAULT_SIGNATURE_METHOD_KEY,
      name: DEFAULT_SIGNATURE_METHOD_KEY.replace(/_/g, " ")
    })
  }
  const sel = selectedKey && String(selectedKey).trim() !== "" ? String(selectedKey).trim() : DEFAULT_SIGNATURE_METHOD_KEY
  return ordered
    .map(
      (sm) =>
        `<option value="${escapeAttr(sm.key_name)}" ${sm.key_name === sel ? " selected" : ""}>${escapeHtml(
          sm.name
        )}</option>`
    )
    .join("")
}

/**
 * Human-readable label for Output / CSV preview (create payload still uses key_name).
 * @param {string} key
 */
function signatureMethodDisplayName(key) {
  const k = key == null ? "" : String(key).trim()
  if (!k) return DEFAULT_SIGNATURE_METHOD_KEY.replace(/_/g, " ")
  for (const sm of signatureMethodOptions) {
    const kn = sm.key_name == null ? "" : String(sm.key_name).trim()
    if (kn === k) {
      const n = sm.name == null ? "" : String(sm.name).trim()
      return n || k.replace(/_/g, " ")
    }
  }
  return k.replace(/_/g, " ")
}

/** @param {Record<string, unknown>} serverRow */
function signatureMethodCsvFromServerRow(serverRow) {
  const raw = serverRow.signature_method
  const parts = Array.isArray(raw) ? raw : raw != null && String(raw).trim() !== "" ? [raw] : []
  return parts
    .map((p) => signatureMethodDisplayName(String(p).trim()))
    .filter(Boolean)
    .join(", ")
}

function renderLiteralsTable(root) {
  const tb = root.querySelector("#uploader-literals-tbody")
  if (!tb) return

  const used = new Set()
  Object.values(state.patientColRole).forEach((v) => {
    if (v && v !== "unique_id" && v !== "ignore" && v !== "dx") used.add(v)
  })

  if (state.literals.language == null || String(state.literals.language).trim() === "") {
    state.literals = { ...state.literals, language: DEFAULT_LANGUAGE_LITERAL }
  }
  if (state.literals.signature_method == null || String(state.literals.signature_method).trim() === "") {
    state.literals = { ...state.literals, signature_method: DEFAULT_SIGNATURE_METHOD_KEY }
  }

  const rows = patientLiteralFieldRows()
    .map((f) => {
      const hidden = used.has(f.value)
      if (f.value === "signature_method") {
        let v = state.literals[f.value] == null ? "" : String(state.literals[f.value])
        if (v.trim() === "") v = DEFAULT_SIGNATURE_METHOD_KEY
        const opts = signatureMethodSelectOptionsHtml(v)
        return `<tr data-literal-for="${escapeAttr(f.value)}" class="uploader-literal-tr ${hidden ? "hidden" : ""}">
        <td class="uploader-literal-label border-b border-gray-100 px-1.5 py-0.5 text-xs text-gray-700 [vertical-align:top] whitespace-nowrap sm:pr-3">${escapeHtml(f.label)}</td>
        <td class="border-b border-gray-100 px-1 py-0.5">
          <select class="uploader-literal-input w-full min-w-0 max-w-md rounded-sm border border-gray-200 bg-white px-1.5 py-0.5 text-xs leading-tight text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/40"
            data-uploader-literal-field="${escapeAttr(f.value)}" ${hidden ? "disabled" : ""}>${opts}</select>
        </td>
      </tr>`
      }
      const v = state.literals[f.value] == null ? "" : String(state.literals[f.value])
      return `<tr data-literal-for="${escapeAttr(f.value)}" class="uploader-literal-tr ${hidden ? "hidden" : ""}">
        <td class="uploader-literal-label border-b border-gray-100 px-1.5 py-0.5 text-xs text-gray-700 [vertical-align:top] whitespace-nowrap sm:pr-3">${escapeHtml(f.label)}</td>
        <td class="border-b border-gray-100 px-1 py-0.5">
          <input type="text" class="uploader-literal-input w-full min-w-0 max-w-md rounded-sm border border-gray-200 bg-white px-1.5 py-0.5 text-xs leading-tight text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/40"
            data-uploader-literal-field="${escapeAttr(f.value)}" value="${escapeAttr(v)}" ${
        hidden ? "disabled" : ""
      } />
        </td>
      </tr>`
    })
    .join("")

  tb.innerHTML = rows
  $$bindLiterals(root)
}

function $$bindLiterals(root) {
  const onChange = () => {
    readLiteralValuesFromDom(root)
    renderOutputTable(root)
  }
  root.querySelectorAll("[data-uploader-literal-field]").forEach((inp) => {
    inp.addEventListener("input", onChange)
    inp.addEventListener("change", onChange)
  })
}

function renderDxTab(root) {
  const wrap = root.querySelector("#uploader-dx-table-wrap")
  if (!wrap) return
  const { headers, rows } = state.dx
  if (!headers.length) {
    wrap.innerHTML =
      '<p class="text-sm text-gray-500">No DX data yet. Choose two CSV files and press <strong>Process</strong> on the Input tab.</p>'
    return
  }
  if (!state.dxColRole || Object.keys(state.dxColRole).length === 0) {
    state.dxColRole = defaultDxRoles(headers)
  }

  const head = `<tr>${headers
    .map(
      (h) =>
        `<th class="${UMAP_TH}">` +
        `<div class="${UMAP_HLABEL} font-normal normal-case" title="${escapeAttr(h)}">${escapeHtml(
          h
        )}</div>` +
        `<select class="uploader-dx-role ${UMAP_SELECT}" data-dx-h="${escapeAttr(
          h
        )}">` +
        `<option value="unique_id" ${state.dxColRole[h] === "unique_id" ? "selected" : ""}>Unique ID</option>` +
        `<option value="dx" ${state.dxColRole[h] === "dx" ? "selected" : ""}>DX</option>` +
        `<option value="ignore" ${state.dxColRole[h] === "ignore" ? "selected" : ""}>Ignore</option>` +
        `</select></th>`
    )
    .join("")}</tr>`

  const body = rows
    .map(
      (row) =>
        `<tr class="${UMAP_TBODY_TR}">${headers
          .map((h) => `<td class="${UMAP_TD}">${escapeHtml(String(row[h] ?? ""))}</td>`)
          .join("")}</tr>`
    )
    .join("")

  wrap.innerHTML = `<div class="w-full min-w-0 max-w-full overflow-x-auto rounded border border-gray-200/80 bg-white"><table class="${UMAP_TABLE_MAP}"><thead class="${UMAP_THEAD}">${head}</thead><tbody class="uploader-map-tbody">${body}</tbody></table></div>`

  wrap.querySelectorAll("select.uploader-dx-role").forEach((sel) => {
    sel.addEventListener("change", (e) => {
      const h = e.target.getAttribute("data-dx-h")
      state.dxColRole[h] = e.target.value
      renderOutputTable(root)
    })
  })
}

function renderPatientTab(root) {
  const wrap = root.querySelector("#uploader-patient-table-wrap")
  if (!wrap) return
  const { headers, rows } = state.patient
  if (!headers.length) {
    wrap.innerHTML =
      '<p class="text-sm text-gray-500">No patient file data yet. Use <strong>Process</strong> on the Input tab.</p>'
    renderLiteralsTable(root)
    return
  }
  if (!state.patientColRole || Object.keys(state.patientColRole).length === 0) {
    state.patientColRole = defaultPatientRoles(headers)
  }
  for (const h of headers) {
    if (state.patientColRole[h] == null) state.patientColRole[h] = "ignore"
  }

  const head = `<tr>${headers
    .map(
      (h) =>
        `<th class="${UMAP_TH}">` +
        `<div class="${UMAP_HLABEL} font-normal normal-case" title="${escapeAttr(h)}">${escapeHtml(
          h
        )}</div>` +
        `<select class="uploader-pat-role ${UMAP_SELECT}" data-pat-h="${escapeAttr(
          h
        )}">${patientColumnSelectOptions(state.patientColRole[h] || "ignore")}</select></th>`
    )
    .join("")}</tr>`

  const body = rows
    .map(
      (row) =>
        `<tr class="${UMAP_TBODY_TR}">${headers
          .map((h) => `<td class="${UMAP_TD}">${escapeHtml(String(row[h] ?? ""))}</td>`)
          .join("")}</tr>`
    )
    .join("")

  wrap.innerHTML = `<div class="w-full min-w-0 max-w-full overflow-x-auto rounded border border-gray-200/80 bg-white"><table class="${UMAP_TABLE_MAP}"><thead class="${UMAP_THEAD}">${head}</thead><tbody>${body}</tbody></table></div>`

  wrap.querySelectorAll("select.uploader-pat-role").forEach((sel) => {
    sel.addEventListener("change", (e) => {
      const h = e.target.getAttribute("data-pat-h")
      state.patientColRole[h] = e.target.value
      readLiteralValuesFromDom(root)
      renderLiteralsTable(root)
      renderOutputTable(root)
    })
  })

  renderLiteralsTable(root)
}

function dxHeaderForRole(role) {
  return Object.keys(state.dxColRole).find((h) => state.dxColRole[h] === role)
}

function patHeaderForRole(role) {
  return Object.keys(state.patientColRole).find((h) => state.patientColRole[h] === role)
}

/** Output + persist: mapped enrollment fields shown as columns (excludes combined dx + patient ehr). */
function mappableFieldCols() {
  return baseMappableEnrollmentFields()
    .map((f) => f.value)
    .filter(
      (name) =>
        !patientUploaderExcludedField(name) && name !== "dx_codes" && name !== "patient_ehr_id"
    )
}

function collectDxCodesForId(uniqueId) {
  const idCol = dxHeaderForRole("unique_id")
  if (!idCol) return []
  const { headers, rows } = state.dx
  const codes = []
  for (const row of rows) {
    if (String(row[idCol] ?? "").trim() !== String(uniqueId).trim()) continue
    for (const h of headers) {
      if (state.dxColRole[h] !== "dx") continue
      const v = row[h]
      if (v != null && String(v).trim() !== "") codes.push(String(v).trim())
    }
  }
  return codes
}

function valueForFieldFromPatientRow(patientRow, field) {
  const col = Object.keys(state.patientColRole).find((h) => state.patientColRole[h] === field)
  if (!col) return null
  return patientRow[col]
}

/**
 * @param {HTMLElement} root
 */
function readScrubFromDom(root) {
  const l = (id) => root.querySelector(`#${id}`)
  state.scrub.stripLineBreaks = true
  state.scrub.hideEmptyUid = true
  state.scrub.dob = "normalize"
  state.scrub.dobDelimiter = "/"
  state.scrub.phone = "off"
  state.scrub.addressGeocode = l("uploader-scrub-geo")?.checked === true
}

/**
 * Keeps state.ehrId (UUID) for payloads and state.ehrName for Output preview.
 * @param {HTMLElement} root
 */
function syncEhrSelectionFromDom(root) {
  const ehr = root.querySelector("#uploader-ehr")
  const id = ehr?.value != null && String(ehr.value).trim() !== "" ? String(ehr.value).trim() : ""
  state.ehrId = id
  if (!id) {
    state.ehrName = ""
    return
  }
  const opt = ehr?.selectedOptions?.[0]
  const label = opt && String(opt.value) === id ? String(opt.textContent || "").trim() : ""
  state.ehrName = label
}

/**
 * @param {Record<string, string>} prow
 * @param {string} field  enrollment key
 * @param {HTMLElement} root
 * @returns {string} html for td inner
 */
function getCellHtmlForField(prow, field, root) {
  readScrubFromDom(root)
  const fromPatient = valueForFieldFromPatientRow(prow, field)
  if (fromPatient != null && String(fromPatient).trim() !== "") {
    let s = String(fromPatient)
    if (state.scrub.stripLineBreaks) s = stripLineBreaks(s)
    if (field === "dob" && state.scrub.dob === "normalize") s = dobNormalize(s) || s
    else if (field === "dob" && state.scrub.dob === "pad_year") s = dobPadYear(s, state.scrub.dobDelimiter) || s
    return escapeHtml(s)
  }
  const lit = state.literals[field]
  const useLiteral = literalWithDefaults(field, lit)
  if (useLiteral != null && String(useLiteral).trim() !== "") {
    let s = String(useLiteral)
    if (state.scrub.stripLineBreaks) s = stripLineBreaks(s)
    if (field === "dob" && state.scrub.dob === "normalize") s = dobNormalize(s) || s
    else if (field === "dob" && state.scrub.dob === "pad_year") s = dobPadYear(s, state.scrub.dobDelimiter) || s
    if (field === "signature_method") {
      return `<em class="italic text-gray-900">${escapeHtml(signatureMethodDisplayName(s.trim()))}</em>`
    }
    return `${escapeHtml(s)} <span class="text-xs text-gray-500">(literal)</span>`
  }
  return "—"
}

/**
 * @param {string} field
 * @param {unknown} lit
 */
function literalWithDefaults(field, lit) {
  if (field === "language" && (lit == null || String(lit).trim() === "")) return DEFAULT_LANGUAGE_LITERAL
  if (field === "signature_method" && (lit == null || String(lit).trim() === ""))
    return DEFAULT_SIGNATURE_METHOD_KEY
  return lit
}

/**
 * @returns {string} extra th cells for phone + geo
 */
function scrubExtraHead() {
  let s = ""
  if (state.scrub.phone === "coalesce_mobile_home") {
    s += `${thLabelHtml("Scrub phone")}${thLabelHtml("Scrub phone type")}`
  } else if (state.scrub.phone === "coalesce_mobile_home_other") {
    s += [
      "Scrub phone",
      "Type 1",
      "Scrub phone 2",
      "Type 2",
      "Scrub phone 3",
      "Type 3"
    ]
      .map((t) => thLabelHtml(t))
      .join("")
  }
  if (state.scrub.addressGeocode) {
    s += ["Scrub street (geo)", "City", "State", "Zip"].map((t) => thLabelHtml(t)).join("")
  }
  return s
}

function thLabelHtml(html) {
  return `<th class="${UMAP_TH}">${html}</th>`
}

function phoneScrubCellsForRow(prow) {
  const p1 = patHeaderForRole("primary_phone")
  const p2 = patHeaderForRole("phone_2_number")
  const p3 = patHeaderForRole("phone_3_number")
  const raw = (h) => (h ? prow[h] : null)
  let v1 = raw(p1)
  let v2 = raw(p2)
  let v3 = raw(p3)
  if (state.scrub.stripLineBreaks) {
    v1 = v1 != null ? stripLineBreaks(String(v1)) : v1
    v2 = v2 != null ? stripLineBreaks(String(v2)) : v2
    v3 = v3 != null ? stripLineBreaks(String(v3)) : v3
  }
  if (state.scrub.phone === "coalesce_mobile_home") {
    const p = coalesceMobileHome(v1, v2)
    return `<td class="${UMAP_OUT_DATA_TD}">${p.phone == null ? "—" : escapeHtml(p.phone)}</td><td class="${UMAP_OUT_DATA_TD}">${p.type ? escapeHtml(p.type) : "—"}</td>`
  }
  if (state.scrub.phone === "coalesce_mobile_home_other") {
    const t = coalesceMobileHomeOther(v1, v2, v3)
    const cells = [
      t.scrub_phone,
      t.scrub_phone_type,
      t.scrub_phone_2,
      t.scrub_phone_type_2,
      t.scrub_phone_3,
      t.scrub_phone_type_3
    ]
    return cells
      .map((c) => {
        if (c == null || c === "") return `<td class="${UMAP_OUT_DATA_TD}">—</td>`
        return `<td class="${UMAP_OUT_DATA_TD}">${escapeHtml(String(c))}</td>`
      })
      .join("")
  }
  return ""
}

function geocodeDisplayCellsForRow(uid) {
  const g = state.geocodeByUid[uid]
  if (!g) {
    return ["—", "—", "—", "—"]
      .map((x) => `<td class="${UMAP_OUT_DATA_TD} text-gray-500">${x}</td>`)
      .join("")
  }
  return [
    g.line1 || "—",
    g.city != null && g.city !== "" ? g.city : "—",
    g.state != null && g.state !== "" ? g.state : "—",
    g.zipCode != null && g.zipCode !== "" ? g.zipCode : "—"
  ]
    .map((x) => `<td class="${UMAP_OUT_DATA_TD}">${escapeHtml(String(x))}</td>`)
    .join("")
}

/**
 * Plain cell value for preview CSV (matches Output table data; empty string when no value).
 * @param {Record<string, string>} prow
 * @param {string} field
 * @param {HTMLElement} root
 */
function plainTextValueForField(prow, field, root) {
  readScrubFromDom(root)
  const fromPatient = valueForFieldFromPatientRow(prow, field)
  if (fromPatient != null && String(fromPatient).trim() !== "") {
    let s = String(fromPatient)
    if (state.scrub.stripLineBreaks) s = stripLineBreaks(s)
    if (field === "dob" && state.scrub.dob === "normalize") s = dobNormalize(s) || s
    else if (field === "dob" && state.scrub.dob === "pad_year") s = dobPadYear(s, state.scrub.dobDelimiter) || s
    return s
  }
  const lit = state.literals[field]
  const useLiteral = literalWithDefaults(field, lit)
  if (useLiteral != null && String(useLiteral).trim() !== "") {
    let s = String(useLiteral)
    if (state.scrub.stripLineBreaks) s = stripLineBreaks(s)
    if (field === "dob" && state.scrub.dob === "normalize") s = dobNormalize(s) || s
    else if (field === "dob" && state.scrub.dob === "pad_year") s = dobPadYear(s, state.scrub.dobDelimiter) || s
    if (field === "signature_method") return signatureMethodDisplayName(s.trim())
    return s
  }
  return ""
}

/** @param {string | number | null | undefined} val */
function csvEscapeCell(val) {
  const s = val == null ? "" : String(val)
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`
  return s
}

/** Always double-quote so spreadsheet apps treat the cell as text (avoids numeric PID / leading zeros). */
function csvEscapeCellAsText(val) {
  const s = val == null ? "" : String(val)
  return `"${s.replace(/"/g, '""')}"`
}

/**
 * @param {HTMLElement} root
 * @returns {{ csv: string } | { error: string }}
 */
function buildOutputPreviewCsv(root) {
  readScrubFromDom(root)
  readLiteralValuesFromDom(root)
  syncEhrSelectionFromDom(root)
  const pidHeader = patHeaderForRole("unique_id")
  if (!pidHeader) {
    return { error: "Map a Unique ID on the Patient tab and DX tab first." }
  }
  if (!dxHeaderForRole("unique_id")) {
    return { error: "Map a Unique ID column on the DX tab." }
  }
  let pRows = state.patient.rows
  if (state.scrub.hideEmptyUid) {
    pRows = pRows.filter((r) => String(r[pidHeader] ?? "").trim() !== "")
  }

  const serverRows = pRows
    .map((prow) => {
      const uid = String(prow[pidHeader] ?? "").trim()
      if (!uid) return null
      return buildEnrollmentRowForServer(prow, uid, root)
    })
    .filter(Boolean)

  const headerRow = UPLOADER_PREVIEW_CSV_HEADERS
  const lines = [headerRow.map(csvEscapeCell).join(",")]
  const batchBags = serverRows.map((r) => ({ ...r }))

  for (let i = 0; i < serverRows.length; i++) {
    const serverRow = serverRows[i]
    const cohort = computeUploaderCohortPreview(batchBags[i], batchBags, state.dxDeviceMapRows)
    const sigCsv = signatureMethodCsvFromServerRow(serverRow)
    const row = headerRow.map((h) => {
      const cell = previewCsvCell(h, serverRow, cohort, sigCsv)
      return h === "pid" || h === "patient_ehr_id" ? csvEscapeCellAsText(cell) : csvEscapeCell(cell)
    })
    lines.push(row.join(","))
  }

  return { csv: lines.join("\r\n") }
}

/**
 * @param {HTMLElement} root
 */
async function downloadOutputPreviewCsv(root) {
  const btn = root.querySelector("#uploader-output-download-csv")
  setUploaderButtonBusy(btn, true)
  await yieldForBrowserPaint()
  try {
    await withBusy(async () => {
      await yieldForBrowserPaint()
      const built = buildOutputPreviewCsv(root)
      if ("error" in built) {
        window.alert(built.error)
        return
      }
      const blob = new Blob([built.csv], { type: "text/csv;charset=utf-8" })
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19)
      a.href = url
      a.download = `uploader-patient-preview-${stamp}.csv`
      a.rel = "noopener"
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    })
  } finally {
    setUploaderButtonBusy(btn, false)
  }
}

async function runGeocodeForAllRows(root) {
  readScrubFromDom(root)
  const key = (root.getAttribute("data-uploader-geocode-key") || "").trim()
  const hStreet = patHeaderForRole("street")
  const pidHeader = patHeaderForRole("unique_id")
  if (!hStreet) {
    window.alert("Map a column to the enrollment field **Street** to geocode full addresses (CLI-style single address column).")
    return
  }
  if (!key) {
    window.alert("Set UPLOADER_GEOCODE_KEY in the app environment, then reload.")
    return
  }
  await withBusy(async () => {
    const pRows = state.patient.rows
    let n = 0
    for (const prow of pRows) {
      const uid = String(prow[pidHeader] ?? "").trim()
      if (!uid) continue
      const raw = prow[hStreet]
      if (raw == null || !String(raw).trim()) {
        state.geocodeByUid[uid] = { line1: "", city: null, state: null, zipCode: null }
        /* eslint no-await-in-loop: 0 */
        continue
      }
      if (isPoboxAddress(String(raw))) {
        state.geocodeByUid[uid] = { line1: String(raw), city: null, state: null, zipCode: null }
        continue
      }
      const j = await fetchGeocodeJson(String(raw), key)
      if (j && j.results && j.results.length) {
        const p = parseGeocodeResult(j.results[0])
        state.geocodeByUid[uid] = p
        n += 1
      } else {
        state.geocodeByUid[uid] = { line1: String(raw), city: null, state: null, zipCode: null }
      }
      await new Promise((r) => setTimeout(r, 60))
    }
    window.alert(
      n > 0
        ? `Geocoding finished. Populated ${n} address row(s).`
        : "No rows geocoded (empty Street, PO boxes, or no API results). Check the Street column and key."
    )
    renderOutputTable(root)
  })
}

/**
 * Raw string for a mapped enrollment field (mirrors getCellHtmlForField without HTML).
 * @param {Record<string, string>} prow
 * @param {string} field
 * @param {HTMLElement} root
 * @returns {string | null}
 */
/**
 * Stored as enrollment.patient_ehr_id: patient column / literal for that field, or Unique ID if none.
 * @param {Record<string, string>} prow
 * @param {string} uid
 * @param {HTMLElement} root
 */
function resolvedPatientEhrId(prow, uid, root) {
  const pehr = rawValueForMappedField(prow, "patient_ehr_id", root)
  if (pehr != null && String(pehr).trim() !== "") return String(pehr).trim()
  return String(uid ?? "").trim()
}

function rawValueForMappedField(prow, field, root) {
  readScrubFromDom(root)
  const fromPatient = valueForFieldFromPatientRow(prow, field)
  if (fromPatient != null && String(fromPatient).trim() !== "") {
    let s = String(fromPatient)
    if (state.scrub.stripLineBreaks) s = stripLineBreaks(s)
    if (field === "dob" && state.scrub.dob === "normalize") s = dobNormalize(s) || s
    else if (field === "dob" && state.scrub.dob === "pad_year") s = dobPadYear(s, state.scrub.dobDelimiter) || s
    return s
  }
  const lit = state.literals[field]
  const useLiteral = literalWithDefaults(field, lit)
  if (useLiteral != null && String(useLiteral).trim() !== "") {
    let s = String(useLiteral)
    if (state.scrub.stripLineBreaks) s = stripLineBreaks(s)
    if (field === "dob" && state.scrub.dob === "normalize") s = dobNormalize(s) || s
    else if (field === "dob" && state.scrub.dob === "pad_year") s = dobPadYear(s, state.scrub.dobDelimiter) || s
    return s
  }
  return null
}

/**
 * @param {Record<string, string>} row
 * @param {Record<string, string>} prow
 */
function applyPhoneScrubToRow(row, prow) {
  const p1 = patHeaderForRole("primary_phone")
  const p2 = patHeaderForRole("phone_2_number")
  const p3 = patHeaderForRole("phone_3_number")
  const raw = (h) => (h ? prow[h] : null)
  let v1 = raw(p1)
  let v2 = raw(p2)
  let v3 = raw(p3)
  if (state.scrub.stripLineBreaks) {
    v1 = v1 != null ? stripLineBreaks(String(v1)) : v1
    v2 = v2 != null ? stripLineBreaks(String(v2)) : v2
    v3 = v3 != null ? stripLineBreaks(String(v3)) : v3
  }
  if (state.scrub.phone === "coalesce_mobile_home") {
    const p = coalesceMobileHome(v1, v2)
    if (p.phone) row.primary_phone = p.phone
    if (p.type) row.primary_phone_type = p.type
    if (v2 && p.phone !== v2) row.phone_2_number = v2
    return
  }
  if (state.scrub.phone === "coalesce_mobile_home_other") {
    const t = coalesceMobileHomeOther(v1, v2, v3)
    if (t.scrub_phone) row.primary_phone = t.scrub_phone
    if (t.scrub_phone_type) row.primary_phone_type = t.scrub_phone_type
    if (t.scrub_phone_2) row.phone_2_number = t.scrub_phone_2
    if (t.scrub_phone_type_2) row.phone_2_type = t.scrub_phone_type_2
    if (t.scrub_phone_3) row.phone_3_number = t.scrub_phone_3
    if (t.scrub_phone_type_3) row.phone_3_type = t.scrub_phone_type_3
    return
  }
  if (v1) row.primary_phone = v1
  if (v2) row.phone_2_number = v2
  if (v3) row.phone_3_number = v3
}

/**
 * @param {Record<string, string>} row
 * @param {string} uid
 */
function applyGeocodeToRow(row, uid) {
  if (!state.scrub.addressGeocode) return
  const g = state.geocodeByUid[uid]
  if (!g) return
  if (g.line1) row.street = g.line1
  if (g.city) row.city = g.city
  if (g.state) row.state = g.state
  if (g.zipCode) row.zip_code = g.zipCode
}

/**
 * One server enrollment row: same shape as the Output tab for this patient row.
 * @param {Record<string, string>} prow
 * @param {string} uid
 * @param {HTMLElement} root
 * @returns {Record<string, unknown>}
 */
function buildEnrollmentRowForServer(prow, uid, root) {
  readScrubFromDom(root)
  readLiteralValuesFromDom(root)
  /** @type {Record<string, unknown>} */
  const row = {}
  row.pid = String(uid ?? "").trim()
  const fromFile = collectDxCodesForId(uid)
  const fromPat = collectPatientDxFromRow(prow)
  row.dx_codes = uniqueOrdered([...fromFile, ...fromPat])
  row.patient_ehr_id = String(resolvedPatientEhrId(prow, uid, root) ?? "").trim()
  for (const field of mappableFieldCols()) {
    if (field === "primary_phone" || field === "phone_2_number" || field === "phone_3_number")
      continue
    const v = rawValueForMappedField(prow, field, root)
    if (v != null && String(v).trim() !== "") {
      row[field] = field === "signature_method" ? [String(v).trim()] : v
    }
  }
  const prov = rawValueForMappedField(prow, "provider_email", root) || (state.providerEmail && state.providerEmail.trim() !== "" ? state.providerEmail : null)
  if (prov) row.provider_email = prov
  applyPhoneScrubToRow(row, prow)
  applyGeocodeToRow(row, uid)
  return row
}

/**
 * @param {HTMLElement} root
 * @returns {{ practice_name: string, ehr_id: string, primary_provider_email: string, notes_html: string, enrollments: object[] } | { error: string }}
 */
function buildCreateEnrollmentsBody(root) {
  syncNotesFromEditor(root)
  const practice = root.querySelector("#uploader-practice")?.value?.trim() || ""
  const email = root.querySelector("#uploader-provider-email")?.value?.trim() || ""
  const ehr = root.querySelector("#uploader-ehr")?.value?.trim() || ""
  if (!practice) return { error: "Enter a practice name on the Input tab." }
  if (!ehr) return { error: "Select an EHR on the Input tab." }
  const pidHeader = patHeaderForRole("unique_id")
  if (!pidHeader) return { error: "Map a Unique ID on the Patient and DX tabs, then use Load CSVs again." }
  if (!dxHeaderForRole("unique_id")) return { error: "Map a Unique ID column on the DX tab." }
  readScrubFromDom(root)
  readLiteralValuesFromDom(root)
  let pRows = state.patient.rows
  if (state.scrub.hideEmptyUid) {
    pRows = pRows.filter((r) => String(r[pidHeader] ?? "").trim() !== "")
  }
  if (!pRows.length) return { error: "No patient rows to import (or all Unique IDs are empty and hidden)." }
  const nIn = root.querySelector("[data-uploader-notes-input]")
  const notesHtml = nIn?.value || ""
  const enrollments = pRows
    .map((prow) => {
      const uid = String(prow[pidHeader] ?? "").trim()
      if (!uid) return null
      return buildEnrollmentRowForServer(prow, uid, root)
    })
    .filter(Boolean)
  if (enrollments.length === 0) return { error: "No rows with a non-empty Unique ID. Check your patient file and mapping." }
  return {
    practice_name: practice,
    ehr_id: ehr,
    primary_provider_email: email,
    notes_html: notesHtml,
    enrollments
  }
}

/**
 * Compact cohort overview + tables (matches filter cohort ideas from whitegloves/_filters).
 * @param {Record<string, number | Record<string, number>> | null | undefined} stats
 * @returns {string} HTML (escaped where needed)
 */
function formatCohortSummaryHtml(stats) {
  if (!stats) return ""
  const esc = escapeHtml
  /** @param {string} k */
  const num = (k) => (stats[k] != null ? stats[k] : 0)

  const th =
    "border-b border-gray-200 px-2 py-1.5 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-600"
  const thR =
    "border-b border-gray-200 px-2 py-1.5 text-right text-[11px] font-semibold uppercase tracking-wide text-gray-600"
  const td = "border-b border-gray-100/90 px-2 py-1.5 text-gray-800"
  const tdR = "border-b border-gray-100/90 px-2 py-1.5 text-right font-mono tabular-nums text-gray-900"
  const wrap =
    "overflow-x-auto rounded-md border border-gray-200/90 bg-white shadow-[inset_0_1px_0_0_rgba(255,255,255,0.6)]"

  /** @type {[string, string][]} */
  const servableFlags = [
    ["65+", "is_sixty_five_plus"],
    ["Enrollable Medicare", "enrollable_medicare"],
    ["Verifiable Med Adv", "verifiable_medadv"],
    ["MA flag", "is_med_adv"],
    ["No DX or insurance", "no_dx_or_insurance"],
    ["Has DX, no insurance", "has_dx_no_insurance"],
    ["No DX, has insurance", "no_dx_has_insurance"]
  ]

  let html =
    `<div class="space-y-4 text-xs sm:text-sm text-gray-800">` +
    `<div class="space-y-1 rounded-md border border-emerald-200/60 bg-white/80 px-2.5 py-2">` +
    `<p class="m-0"><span class="text-gray-600">Total enrollments</span> · <strong>${num("total")}</strong></p>` +
    `<p class="m-0"><span class="text-gray-600">With primary insurance name</span> · <strong>${num("with_primary_payer")}</strong> · ` +
    `<span class="text-gray-600">missing/blank</span> · <strong>${num("missing_primary_payer")}</strong></p>` +
    `<p class="m-0"><span class="text-gray-600">With at least one DX code</span> · <strong>${num("with_at_least_one_dx_code")}</strong></p>` +
    `</div>` +
    `<div>` +
    `<p class="m-0 mb-1 font-semibold text-gray-900">Servable-related flags</p>` +
    `<p class="m-0 mb-2 text-[11px] leading-snug text-gray-600">Import only — eligibility has not been run yet.</p>` +
    `<div class="${wrap}">` +
    `<table class="app-data-table w-full min-w-0 table-auto border-collapse text-xs">` +
    `<thead><tr class="bg-gray-50/95"><th class="${th}">Flag</th><th class="${thR}">Count</th></tr></thead>` +
    `<tbody>` +
    servableFlags
      .map(
        ([label, key]) =>
          `<tr class="even:bg-slate-50/40 hover:bg-rose-50/15">` +
            `<td class="${td}">${esc(label)}</td>` +
            `<td class="${tdR}">${num(key)}</td>` +
          `</tr>`
      )
      .join("") +
    `</tbody></table></div></div>`

  const elig = stats.eligibilityresult
  if (elig && typeof elig === "object" && Object.keys(elig).length > 0) {
    const rows = Object.keys(elig)
      .sort()
      .map(
        (k) =>
          `<tr class="even:bg-slate-50/40 hover:bg-rose-50/15">` +
            `<td class="${td} font-mono text-[11px]">${esc(k)}</td>` +
            `<td class="${tdR}">${elig[k]}</td>` +
          `</tr>`
      )
      .join("")
    html +=
      `<div>` +
      `<p class="m-0 mb-1 font-semibold text-gray-900">Eligibility result</p>` +
      `<p class="m-0 mb-2 text-[11px] leading-snug text-gray-600">Usually all <span class="font-mono">UNPROCESSED</span> until you run eligibility on this whiteglove.</p>` +
      `<div class="${wrap}">` +
      `<table class="app-data-table w-full min-w-0 table-auto border-collapse text-xs">` +
      `<thead><tr class="bg-gray-50/95"><th class="${th}">Code</th><th class="${thR}">Count</th></tr></thead>` +
      `<tbody>${rows}</tbody></table></div></div>`
  }

  html += `</div>`
  return html
}

/**
 * @param {HTMLElement} root
 * @param {object | null} res
 */
function showCreateSummary(root, res) {
  const errEl = root.querySelector("#uploader-output-error")
  const sum = root.querySelector("#uploader-output-summary")
  const title = root.querySelector("#uploader-output-summary-title")
  const msg = root.querySelector("#uploader-output-summary-message")
  const coh = root.querySelector("#uploader-output-summary-cohorts")
  const payA = root.querySelector("#uploader-output-payers-link")
  const wgA = root.querySelector("#uploader-output-wg-link")
  if (errEl) errEl.classList.add("hidden")
  if (sum) sum.classList.add("hidden")
  if (!res) return
  if (res.success) {
    if (title) title.textContent = "Import created"
    if (msg) msg.textContent = res.message || ""
    if (coh) {
      coh.innerHTML = formatCohortSummaryHtml(res.stats)
    }
    const id = res.whiteglove_id
    const payTpl = root.getAttribute("data-uploader-payers-template") || ""
    const wgTpl = root.getAttribute("data-uploader-wg-template") || ""
    const zid = "00000000-0000-0000-0000-000000000000"
    if (payA && id) payA.setAttribute("href", payTpl.replace(zid, id))
    if (wgA && id) wgA.setAttribute("href", wgTpl.replace(zid, id))
    if (sum) sum.classList.remove("hidden")
  } else {
    if (errEl) {
      errEl.textContent = res.error || "Request failed."
      errEl.classList.remove("hidden")
    }
  }
}

/**
 * @param {HTMLElement} root
 */
/**
 * Lets the browser paint DOM updates (e.g. disabled button) before long synchronous work on the main thread.
 * @returns {Promise<void>}
 */
function yieldForBrowserPaint() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve())
    })
  })
}

/**
 * Disables the button, shows inline spinner, sets aria-busy (avoids repeat clicks during heavy work).
 * @param {HTMLButtonElement | null} btn
 * @param {boolean} busy
 */
function setUploaderButtonBusy(btn, busy) {
  if (!btn) return
  btn.disabled = busy
  if (busy) btn.setAttribute("aria-busy", "true")
  else btn.removeAttribute("aria-busy")
  btn.querySelector(".uploader-wiz-spinner")?.classList.toggle("hidden", !busy)
}

async function onCreateEnrollmentsClick(root) {
  const btn = root.querySelector("#uploader-output-create")
  const errEl = root.querySelector("#uploader-output-error")
  if (errEl) {
    errEl.classList.add("hidden")
  }
  setUploaderButtonBusy(btn, true)
  await yieldForBrowserPaint()
  try {
    await withBusy(async () => {
      await yieldForBrowserPaint()
      const body = buildCreateEnrollmentsBody(root)
      if (body && typeof body.error === "string") {
        if (errEl) {
          errEl.textContent = body.error
          errEl.classList.remove("hidden")
        } else {
          window.alert(body.error)
        }
        return
      }
      const url = root.getAttribute("data-uploader-create-url")
      if (!url) {
        if (errEl) {
          errEl.textContent = "Missing uploader create URL (data attribute)."
          errEl.classList.remove("hidden")
        }
        return
      }
      const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": token || ""
        },
        body: JSON.stringify({
          practice_name: body.practice_name,
          ehr_id: body.ehr_id,
          primary_provider_email: body.primary_provider_email,
          notes_html: body.notes_html,
          enrollments: body.enrollments
        })
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.success) {
        showCreateSummary(root, { success: true, ...data })
      } else {
        const err = data.error || res.statusText || "Save failed"
        if (errEl) {
          errEl.textContent = err
          errEl.classList.remove("hidden")
        } else {
          window.alert(err)
        }
      }
    })
  } catch (e) {
    const msg = e && e.message ? e.message : String(e)
    if (errEl) {
      errEl.textContent = msg
      errEl.classList.remove("hidden")
    } else {
      window.alert(msg)
    }
  } finally {
    setUploaderButtonBusy(btn, false)
  }
}

function renderOutputTable(root) {
  const wrap = root.querySelector("#uploader-output-table-wrap")
  if (!wrap) return
  const pidHeader = patHeaderForRole("unique_id")
  if (!pidHeader) {
    wrap.innerHTML = '<p class="text-sm text-amber-800">Choose <strong>Unique ID</strong> for one patient file column, then set DX tab Unique ID as well.</p>'
    return
  }
  if (!dxHeaderForRole("unique_id")) {
    wrap.innerHTML = '<p class="text-sm text-amber-800">Set <strong>Unique ID</strong> for one column on the DX tab.</p>'
    return
  }
  let pRows = state.patient.rows
  readScrubFromDom(root)
  readLiteralValuesFromDom(root)
  syncEhrSelectionFromDom(root)
  if (state.scrub.hideEmptyUid) {
    pRows = pRows.filter((r) => String(r[pidHeader] ?? "").trim() !== "")
  }
  const fieldCols = mappableFieldCols()
  const fieldMeta = (v) => enrollmentFields.find((x) => x.value === v) || { label: v, value: v }
  const thLabel = (html) => `<th class="${UMAP_TH}">${html}</th>`
  const head = `<tr>
    ${thLabel("Unique ID")}
    <th class="${UMAP_TH} min-w-[14rem]">DX codes</th>
    ${thLabel(escapeHtml(fieldMeta("patient_ehr_id").label))}
    ${fieldCols.map((f) => thLabel(escapeHtml(fieldMeta(f).label))).join("")}
    ${scrubExtraHead()}
  </tr>`

  const body = pRows
    .map((prow) => {
      const uid = String(prow[pidHeader] ?? "").trim()
      if (!uid) return ""
      const fromFile = collectDxCodesForId(uid)
      const fromPat = collectPatientDxFromRow(prow)
      const dxList = uniqueOrdered([...fromFile, ...fromPat])
      let dxJoined = dxList.length ? dxList.join(", ") : "—"
      if (state.scrub.stripLineBreaks && dxJoined !== "—") dxJoined = stripLineBreaks(dxJoined)
      const dxText = escapeHtml(dxJoined)
      const dxCell = dxText
      const rawPehr = rawValueForMappedField(prow, "patient_ehr_id", root)
      const patientEhrCell =
        rawPehr != null && String(rawPehr).trim() !== ""
          ? getCellHtmlForField(prow, "patient_ehr_id", root)
          : escapeHtml(uid)
      const patientEhrTdClass = UMAP_OUT_DATA_TD
      const cells = fieldCols
        .map((field) => {
          const cellHtml = getCellHtmlForField(prow, field, root)
          return `<td class="${UMAP_OUT_DATA_TD}">${cellHtml}</td>`
        })
        .join("")
      const extraPhone = state.scrub.phone !== "off" ? phoneScrubCellsForRow(prow) : ""
      const extraGeo = state.scrub.addressGeocode ? geocodeDisplayCellsForRow(uid) : ""
      return `<tr class="${UMAP_TBODY_TR}">
        <td class="${UMAP_OUT_TD_MONO}">${escapeHtml(uid)}</td>
        <td class="${UMAP_OUT_TD_DX}">${dxCell}</td>
        <td class="${patientEhrTdClass}">${patientEhrCell}</td>
        ${cells}
        ${extraPhone}
        ${extraGeo}
      </tr>`
    })
    .join("")

  const scrubNote =
    '<p class="mb-3 text-xs text-gray-500">One row per patient file. Line breaks are stripped in preview; DOB normalizes to YYYY-MM-DD (two-digit years use plausible past birth years). Rows with empty Unique ID are hidden. <strong>dx_codes</strong> = DX file + patient columns set to <strong>DX (→ dx codes)</strong>, comma-separated, deduped. Optional geocoding (above) adds columns per Unique ID when <strong>Street</strong> is mapped. <strong>Patient ehr</strong> uses the patient column (or literal) mapped to that field when set; otherwise it defaults to the row&rsquo;s <strong>Unique ID</strong>. The EHR system for the whiteglove is chosen on the Input tab only (<code class="rounded bg-white/80 px-0.5">ehr_id</code> on create). <strong>Signature method</strong> shows the selected method name from the Patient tab dropdown; create still sends the method key.</p>'

  wrap.innerHTML = `${scrubNote}
  <div class="uploader-output-table-scroll w-full min-w-0 max-w-full overflow-x-auto rounded border border-gray-200/80 bg-white">
  <table class="${UMAP_TABLE_MAP} min-w-[80rem]"><thead class="sticky top-0 z-10 border-b border-gray-200/90 ${UMAP_THEAD} bg-gray-50/98 shadow-sm backdrop-blur-sm">${head}</thead><tbody>${body}</tbody></table></div>`
}

async function runInputProcess(root) {
  const dxF = root.querySelector("#uploader-dx-file")
  const pF = root.querySelector("#uploader-patient-file")
  const proc = root.querySelector("#uploader-input-process")
  if (!dxF || !pF) return
  if (!dxF.files?.[0] || !pF.files?.[0]) {
    window.alert("Please select both a DX file and a patient (CSV) file.")
    return
  }
  setUploaderButtonBusy(proc, true)
  await yieldForBrowserPaint()
  syncNotesFromEditor(root)
  const practice = root.querySelector("#uploader-practice")
  const email = root.querySelector("#uploader-provider-email")
  state.practiceName = practice?.value?.trim() || ""
  state.providerEmail = email?.value?.trim() || ""
  syncEhrSelectionFromDom(root)
  const nIn = root.querySelector("[data-uploader-notes-input]")
  state.notesHtml = nIn?.value || ""

  let parseFailed = false
  try {
    await withBusy(async () => {
      await yieldForBrowserPaint()
      try {
        const [dxResult, pResult] = await Promise.all([parseCsvFile(dxF.files[0]), parseCsvFile(pF.files[0])])
        if (dxResult.errors?.length) {
          /* eslint no-console: 0 */
          console.warn("DX parse issues", dxResult.errors)
        }
        if (pResult.errors?.length) {
          console.warn("Patient parse issues", pResult.errors)
        }
        const dxMeta = dxResult.meta
        const pMeta = pResult.meta
        state.dx = { headers: dxMeta.fields || [], rows: dxResult.data || [] }
        state.patient = { headers: pMeta.fields || [], rows: pResult.data || [] }
        state.geocodeByUid = {}
        state.dxColRole = automapDxFileRoles(state.dx.headers)
        state.patientColRole = automapPatientFileRoles(state.patient.headers)
      } catch (e) {
        window.alert("Could not parse CSV: " + (e && e.message ? e.message : e))
        parseFailed = true
      }
    })
    if (parseFailed) return
    await withBusy(async () => {
      await yieldForBrowserPaint()
      readLiteralValuesFromDom(root)
      renderDxTab(root)
      renderPatientTab(root)
      renderOutputTable(root)
    })
  } finally {
    setUploaderButtonBusy(proc, false)
  }
}

function syncNotesFromEditor(root) {
  const ed = root.querySelector("[data-uploader-notes-editor]")
  const hi = root.querySelector("[data-uploader-notes-input]")
  if (ed && hi) hi.value = ed.innerHTML
}

function bindEditorToolbar(root) {
  root.querySelectorAll("[data-uploader-editor-cmd]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.preventDefault()
      const cmd = e.currentTarget.getAttribute("data-uploader-editor-cmd")
      const ed = root.querySelector("[data-uploader-notes-editor]")
      if (!ed) return
      ed.focus()
      try {
        document.execCommand(cmd, false, null)
      } catch (_e) {
        /* ignore */
      }
      syncNotesFromEditor(root)
    })
  })
  const ed = root.querySelector("[data-uploader-notes-editor]")
  if (ed) {
    ed.addEventListener("input", () => syncNotesFromEditor(root))
    ed.addEventListener("blur", () => syncNotesFromEditor(root))
  }
}

/**
 * @param {HTMLElement} root
 */
export function initUploaderWizard() {
  const root = document.getElementById("uploader-wizard")
  if (!root) return

  const el = document.getElementById("uploader-enrollment-fields-json")
  if (!el || !el.textContent) return
  try {
    enrollmentFields = JSON.parse(el.textContent)
  } catch (_e) {
    enrollmentFields = []
  }

  const smJson = document.getElementById("uploader-signature-methods-json")
  try {
    signatureMethodOptions = smJson?.textContent ? JSON.parse(smJson.textContent) : []
    if (!Array.isArray(signatureMethodOptions)) signatureMethodOptions = []
  } catch (_e) {
    signatureMethodOptions = []
  }

  Object.assign(state, {
    dx: { headers: [], rows: [] },
    patient: { headers: [], rows: [] },
    dxColRole: {},
    patientColRole: {},
    literals: { language: DEFAULT_LANGUAGE_LITERAL, signature_method: DEFAULT_SIGNATURE_METHOD_KEY },
    geocodeByUid: {},
    /** Parsed `db/data/dx_code_devices.csv` via uploader page JSON (prefix → device, chronic_care). */
    dxDeviceMapRows: [],
    scrub: {
      stripLineBreaks: true,
      hideEmptyUid: true,
      dob: "normalize",
      dobDelimiter: "/",
      phone: "off",
      addressGeocode: false
    } // strip/hide/dob/phone fixed; only geocode is toggled in the Output tab UI
  })

  try {
    const raw = document.getElementById("uploader-dx-device-map-json")?.textContent
    state.dxDeviceMapRows = raw ? parseDxDeviceMapJson(JSON.parse(raw)) : []
  } catch (_e) {
    state.dxDeviceMapRows = []
  }

  bindEditorToolbar(root)

  const ehr = root.querySelector("#uploader-ehr")
  if (ehr) {
    syncEhrSelectionFromDom(root)
    ehr.addEventListener("change", () => {
      syncEhrSelectionFromDom(root)
      renderOutputTable(root)
    })
  }

  const proc = root.querySelector("#uploader-input-process")
  if (proc) {
    proc.addEventListener("click", (e) => {
      e.preventDefault()
      void runInputProcess(root)
    })
  }

  const outBtn = root.querySelector("#uploader-output-create")
  if (outBtn) {
    outBtn.addEventListener("click", (e) => {
      e.preventDefault()
      void onCreateEnrollmentsClick(root)
    })
  }

  const dlCsv = root.querySelector("#uploader-output-download-csv")
  if (dlCsv) {
    dlCsv.addEventListener("click", (e) => {
      e.preventDefault()
      void downloadOutputPreviewCsv(root)
    })
  }

  const geoHint = root.querySelector("#uploader-scrub-geo-hint")
  const gkey = (root.getAttribute("data-uploader-geocode-key") || "").trim()
  if (geoHint) {
    geoHint.hidden = gkey.length > 0
  }

  root.querySelectorAll("[data-uploader-scrub-input]").forEach((el) => {
    el.addEventListener("change", () => {
      readScrubFromDom(root)
      renderOutputTable(root)
    })
    el.addEventListener("input", () => {
      readScrubFromDom(root)
      renderOutputTable(root)
    })
  })
  const geoRun = root.querySelector("#uploader-scrub-geo-run")
  if (geoRun) {
    geoRun.addEventListener("click", (e) => {
      e.preventDefault()
      runGeocodeForAllRows(root)
    })
  }

  renderLiteralsTable(root)
  renderDxTab(root)
  renderPatientTab(root)
  renderOutputTable(root)
}
